import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as PR from "../../../prime-react/index";
import "./Analytics.scss";
import Layout from '../../../layouts/Layout';
import { useDispatch, useSelector } from 'react-redux';
import { getAlertsCount, getGroupsNames, getLocations, getSports } from '../../../services/api';
import AnalyticsGraph from './AnalyticsGraph';
import FindSuspiciousReport from './FIndSuspiciousReport';
import { resetDateRange, resetPlayerDateRange, resetMarketDateRange, storeDateRange, storePlayerDateRange, storeMarketDateRange } from '../../../store/filter.slice';
import { areDateRangesEqual, defaultDateRange } from '../../../utils/reuse';
import moment from 'moment';

const AnalystAnalytics = () => {
    const sessionData = useSelector(state => state.auth.sessionData);
    const memberAnalyticsState = useSelector(state => state.memberAnalyticsFilter);
    const headers = useMemo(() => {
        return { sessionid: sessionData.sessionId };
    }, [sessionData.sessionId]);
    const dispatch = useDispatch();
    const [membersData, setMembersData] = useState([]);
    const [sports, setSports] = useState([]);
    const [locations, setLocations] = useState([]);
    const [dates, setDates] = useState(memberAnalyticsState.dateRange);
    const [playerDates, setPlayerDates] = useState(memberAnalyticsState.playerDateRange);
    const [marketDates, setMarketDates] = useState(memberAnalyticsState.marketDateRange);
    const [keyMetrics, setKeyMetrics] = useState(null);

    const getMetricHandler = useCallback(() => {
        const obj = {
            type: "tsar",
        };
        if (dates && dates?.length > 0) {
            const startDate = moment(dates[0]).format('YYYY-MM-DD');
            const endDate = dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : startDate;
            obj.startDate = startDate;
            obj.endDate = endDate;
        }
        getAlertsCount(obj, headers, dispatch, response => {
            if (response.result === 'SUCCESS') {
                const responseData = response.data;
                setKeyMetrics(responseData);
            }
        })
    }, [dispatch, headers, dates]);

    // Get list of all the membaers
    const getGroupsHandler = useCallback(() => {
        getGroupsNames(headers, dispatch, response => {
            if (response.result === 'SUCCESS') {   
                const responseData = response?.data ? response?.data?.sort((a, b) => a.name.localeCompare(b.name)) : [] ;
                const filterData = responseData.filter(r => r.status === 'active');
                setMembersData(filterData);
            }
        })
    }, [dispatch, headers]);

    // Get list of all the sports
    const getSportsHandler = useCallback(() => {
        getSports(null, headers, dispatch, response => {
            if (response.result === 'SUCCESS') {
                const responseData = response.data;
                const sports = responseData;
                const result = (sports?.length > 0) ? sports?.sort((a, b) => a.name.localeCompare(b.name)) : [];
                setSports(result);
            } else {
                setSports([]);
            }
        })
    }, [dispatch, headers]);
    const getLocationHandler = useCallback(() => {
        getLocations(headers, dispatch, response => {
            if (response.result === 'SUCCESS') {   
                const responseData = response.data;
                    const locations = responseData;  
                    const result = (locations?.length > 0) ? locations?.sort((a, b) => a.name.localeCompare(b.name)) : [];
                    setLocations(result);
            } else {
                setLocations([]);
                }
        })
    }, [dispatch, headers]);    

    useEffect(() => {
        getMetricHandler();
        getSportsHandler();
        getLocationHandler();
        getGroupsHandler();
    },[getMetricHandler, getGroupsHandler, getLocationHandler, getSportsHandler]);
    
    useEffect(() => {
        setDates(memberAnalyticsState.dateRange);
    }, [memberAnalyticsState.dateRange]);
    useEffect(() => {
        setPlayerDates(memberAnalyticsState.playerDateRange);
    }, [memberAnalyticsState.playerDateRange]);
    useEffect(() => {
        setMarketDates(memberAnalyticsState.marketDateRange);
    }, [memberAnalyticsState.marketDateRange]);
    const setPlayerDateHandler = (value) => {
        if(value === 'remove'){
            dispatch(resetPlayerDateRange())
        } else {
          dispatch(storePlayerDateRange(value))
        }
    }
    const setMarketDateHandler = (value) => {
        if(value === 'remove'){
            dispatch(resetMarketDateRange())
        } else {
          dispatch(storeMarketDateRange(value))
        }
    }
    const calenderHandler = (value) => {
        dispatch(storeDateRange(value))
    }
    const resetDateHandler = () => {
        dispatch(resetDateRange())
    }
    const isDefaultDateRange = areDateRangesEqual(dates, defaultDateRange);
    return (
        <>
            <Layout backBtn={"home"} backBtnLabel="Home">
                <section className="my-analytics-tab">
                    <div className="m-0 flex align-items-center justify-content-between">
                        <div className='heading-card'>
                            <h1>Member analytics</h1>
                        </div>
                        <div className="card custom-clear-calendar">
                            <PR.Calendar 
                                value={dates} 
                                onChange={(e) => calenderHandler(e.value)} 
                                selectionMode="range" 
                                iconPos="left" 
                                readOnlyInput 
                                showIcon 
                                className='date-calendar' 
                                placeholder='Date'
                                dateFormat="dd/mm/yy"
                                maxDate={new Date()}
                            />
                            {!isDefaultDateRange &&
                                <div className='custom-clear'>
                                    <i className="pi pi-times text-sm" onClick={() => resetDateHandler()}></i>
                                </div>
                            }
                            
                        </div>
                    </div>
                    <div className="analytics-wrapper">
                        <div className="grid">
                            <div className="col-4">
                                <div className="info-box">
                                    <div className="flex align-items-start">
                                        <div className='pr-2'>
                                            <h4>Total Alerts Raised</h4>
                                            <p>The number of alerts raised by all groups in a given date range</p>
                                        </div>
                                    </div>
                                    <div className="flex mt-3 align-items-center">
                                        <h2>{keyMetrics?.suspiciousAlertRate?.[0]?.alertCount
                                            ? keyMetrics.suspiciousAlertRate[0].alertCount
                                            : 0}
                                        </h2>
                                    </div>
                                </div>
                            </div>
                            <div className="col-4">
                                <div className="info-box">
                                    <div className="flex align-items-start">
                                        <div className='pr-2'>
                                            <h4>Total Suspicious Alerts Raised</h4>
                                            <p>The number of suspicious alerts raised by all groups in a given date range</p>
                                        </div>
                                    </div>
                                    <div className="flex mt-3 align-items-center">
                                        <h2>{keyMetrics?.suspiciousAlertRate?.[0]?.suspiciousCount
                                            ? keyMetrics.suspiciousAlertRate[0].suspiciousCount
                                            : 0}
                                        </h2>
                                    </div>
                                </div>
                            </div>
                            <div className="col-4">
                                <div className="info-box">
                                    <div className="flex align-items-start">
                                        <div className='pr-2'>
                                            <h4>Suspicious Rate</h4>
                                            <p>The % of alerts that deemed suspicious by all groups in a given date range</p>
                                        </div>
                                    </div>
                                    <div className="flex mt-3 align-items-center">
                                        <h2>
                                            {keyMetrics?.suspiciousAlertRate?.[0]?.suspiciousRate
                                                ? keyMetrics.suspiciousAlertRate[0].suspiciousRate.toFixed(1)
                                                : 0}%
                                        </h2>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="hr-line"></div>
                        <div className="grid align-items-start mt-2">

                            {/* Raised Alert Chart */}
                            <div className="col-6">
                                <AnalyticsGraph graphType={'tarm'} graphColor={'#FFCD02'} secondGraphColor={'#ba3d40'} responseKey={'alertsRaisedPerMember'} graphCountKey={'alertCount'} secondGraphCountKey={'suspiciousCount'} graphNameKey={'groupName'} optionsData={membersData} placeholder={'Select Members'} dates={dates}></AnalyticsGraph>
                            </div>
                            {/* Response Rate Chart */}
                            <div className="col-6">
                                <AnalyticsGraph graphType={'rrpm'} graphColor={'#FFCD02'} responseKey={'responseRatePerMember'} graphCountKey={'responseRate'} graphNameKey={'groupName'}  optionsData={membersData} placeholder={'Select Members'} dates={dates}></AnalyticsGraph>
                            </div>

                            {/* Total Alerts by Sport */}
                            <div className="col-6">
                                <AnalyticsGraph graphType={'taps'} graphColor={'#FFCD02'} secondGraphColor={'#ba3d40'} responseKey={'totalAlertsPerSport'}  graphCountKey={'alertCount'} secondGraphCountKey={'suspiciousCount'} graphNameKey={'sportName'}  optionsData={sports} placeholder={'Select Sports'} dates={dates}></AnalyticsGraph>
                            </div>

                            {/* Total Alerts by Location */}
                            <div className="col-6">
                                <AnalyticsGraph graphType={'tapl'} graphColor={'#FFCD02'} responseKey={'totalAlertsPerLocation'} graphCountKey={'alertCount'} graphNameKey={'locationName'}  optionsData={locations} placeholder={'Select Locations'} dates={dates}></AnalyticsGraph>
                            </div>

                            {/* Tennis Players of Concern */}
                            <div className="col-6">
                                <AnalyticsGraph graphType={'tpc'} graphColor={'#FFCD02'} responseKey={'tennisPlayersOfConcern'} graphCountKey={'tagCount'} graphNameKey={'tagName'}  optionsData={[1]} placeholder={''} dates={dates}></AnalyticsGraph>
                            </div>

                            {/* Alerts Raised Per Sport For Member */}
                            <div className="col-6">
                                <AnalyticsGraph graphType={'arps'} graphColor={'#FFCD02'} responseKey={'alertsRaisedPerSport'} graphCountKey={'alertCount'} graphNameKey={'sportName'} memberOptions={membersData} optionsData={sports} placeholder={'Select Sports'} dates={dates}></AnalyticsGraph>
                            </div>

                            <div className="col-12">
                                <div className="hr-line mt-4 mb-4"></div>
                            </div>

                            {/* Athletes/Teams of Concern */}
                            <div className="col-12">
                                <AnalyticsGraph graphType={'pcps'} graphColor={'#7C7C7C'} responseKey={'playersOfConcernPerSport'} graphCountKey={'tagCount'} graphNameKey={'tagName'}  optionsData={sports} placeholder={'Select Sports'} dates={playerDates} isPlayer={true} isDetailScreen={true} setPlayerDateHandler={setPlayerDateHandler}></AnalyticsGraph>
                            </div>

                            {/* Markets of Concern */}
                            <div className="col-12">
                                <AnalyticsGraph graphType={'mcps'} graphColor={'#7C7C7C'} responseKey={'marketsOfConcernPerSport'} graphCountKey={'tagCount'} graphNameKey={'tagName'}  optionsData={sports} placeholder={'Select Sports'} dates={marketDates} isPlayer={true} isDetailScreen={true} setPlayerDateHandler={setMarketDateHandler} locations={locations}></AnalyticsGraph>
                            </div>

                            {/* Reports Sent Regulators/SGBs */}
                            <FindSuspiciousReport/>

                        </div>
                    </div>
                </section>
            </Layout>
           
        </>
    );
};

export default AnalystAnalytics;
